import { isAfter, isSameDay } from 'date-fns'
import { DateTime } from 'luxon'

export * from './vehicle-registration-number'

type FormikValidationFn = (value: string) => FormikValidationResult
type FormikValidationResult = string | undefined

export const urlRegexp = /^(https:\/\/)(([A-z0-9-_]+)\.)+(\S)+$/
export const phoneRegexp = /^(?:[-\d\s]+|[+\s][-\d\s]+|\s[+][-\d\s]+|[+])$/i
export const phonePrefixRegexp = /^([+]|[0]{2})\d+/i
export const emailRegexp = /^\S+@\S+\.\S+$/
const MAX_SAFE_INTEGER_JAVA = 2147483647

// By default, all our formik validators makes their field required.
// To make it optional, wrap the validator in this helper function.
export function formikValidationOptionalFn(fn: FormikValidationFn): FormikValidationFn {
  return (value: string): FormikValidationResult => {
    return value ? fn(value) : undefined
  }
}

export function formikValidationRequired(value: string, text: string): FormikValidationResult {
  return value ? undefined : text
}

export function formikValidationNoWhitespaces(value: string, text: string): FormikValidationResult {
  return value && /\s/g.test(value) ? text : undefined
}

export function formikValidationNumber(
  value: string | null | undefined,
  text: string,
  bigInteger?: boolean
): FormikValidationResult {
  if (value === null || value?.length === 0 || Number.isNaN(Number(value))) {
    // Not a number
    return text
  } else if (!bigInteger && (-MAX_SAFE_INTEGER_JAVA > Number(value) || Number(value) > MAX_SAFE_INTEGER_JAVA)) {
    // Number out of range
    return text
  } else {
    return undefined
  }
}

export function formikOnlyNumbers(value: string, text: string): FormikValidationResult {
  const regex = /^[0-9]+$/ // Matches only numeric characters
  return regex.test(value) ? undefined : text
}

export function formikValidateNumberInRange(
  value: number,
  from: number,
  to: number,
  text: string
): FormikValidationResult {
  return value < from || value > to ? text : undefined
}

export function formikValidateEmail(value: string, text: string): FormikValidationResult {
  return emailRegexp.test(value) ? undefined : text
}

export function formikValidationPhone(value: string, text: string): FormikValidationResult {
  return phoneRegexp.test(value) ? undefined : text
}

export function formikValidationPhonePrefix(
  value: string,
  phoneText: string,
  phonePrefixText: string
): FormikValidationResult {
  return formikValidationPhone(value, phoneText) || (phonePrefixRegexp.test(value) ? undefined : phonePrefixText)
}

export function formikValidationZipRange(min: number, max: number, text: string): FormikValidationFn {
  return (value: string): FormikValidationResult => {
    const parsed = Number.parseInt(value)
    const valid = parsed >= min && parsed <= max
    return valid ? undefined : text
  }
}

const isNorwegianZipCode = (zipCode: string) => /^\d{4}$/i.test(zipCode)
const isSwedishZipCode = (zipCode: string) => /^\d{5}$/i.test(zipCode.replaceAll(' ', ''))
const isFinnishZipCode = (zipCode: string) => /^\d{5}$/i.test(zipCode)
const isIslandicZipCode = (zipCode: string) => /^\d{3}$/i.test(zipCode)
const isUkZipCode = (zipCode: string) => /^\w{5,7}$/i.test(zipCode.replaceAll(' ', ''))

export const formikValidationZip = (value: string, text: string): FormikValidationResult => {
  const norwegianZipCode = isNorwegianZipCode(value)
  const swedishZipCode = isSwedishZipCode(value)
  const finnishZipCode = isFinnishZipCode(value)
  const islandicZipCode = isIslandicZipCode(value)
  const ukZipCode = isUkZipCode(value)
  const valid = norwegianZipCode || swedishZipCode || finnishZipCode || ukZipCode || islandicZipCode
  return valid ? undefined : text
}

export function formikIsValidDate(date: string | null) {
  return (DateTime.isDateTime(date) && date.isValid) || (date && DateTime.fromISO(date).isValid)
}

export function formikIsValidYYYYMMDDFormat(date: string | null) {
  const regex = /^\d{4}-\d{2}-\d{2}$/
  return date?.match(regex) !== null
}

export const isValidYYYYMMDDDate = (date: string | null): boolean =>
  Boolean(formikIsValidYYYYMMDDFormat(date) && formikIsValidDate(date))

export function formikIsValidYYYYMMDDDate(date: string | null, text: string): FormikValidationResult {
  return isValidYYYYMMDDDate(date) ? undefined : text
}

export function formikIsValidHHMMTime(time: string, text: string): FormikValidationResult {
  const regex = /^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/
  const valid = time.match(regex) !== null
  return valid ? undefined : text
}
export function formikIsValidMMSSTime(time: string, text: string): FormikValidationResult {
  const regex = /^[0-5][0-9]:[0-5][0-9]$/
  const valid = time.match(regex) !== null
  return valid ? undefined : text
}

export function formikDateIsNotInPast(date: string | null, text: string): FormikValidationResult {
  if (!date) return text
  const yesterday = DateTime.now().minus({ days: 1 })
  const isValidDate = formikIsValidDate(date)
  return isValidDate && isAfter(new Date(date), yesterday.toJSDate()) ? undefined : text
}

const startOfDay = (date: string | DateTime | undefined): DateTime =>
  (date === undefined ? DateTime.now() : typeof date === 'string' ? DateTime.fromISO(date) : date).startOf('day')

export function formikDateIsSameOrNextDay(
  date: string | null,
  referenceDate: string | DateTime,
  text: string
): FormikValidationResult {
  if (!date || !formikIsValidDate(date)) return text
  const day = startOfDay(date)
  const reference = startOfDay(referenceDate)
  // luxon's equals() also compares zone and locale, so compare the instants instead
  return day.hasSame(reference, 'day') || day.hasSame(reference.plus({ days: 1 }), 'day') ? undefined : text
}

export function formikDateTimeIsNotInPast(
  validDate: string | null,
  validTime: string | null,
  text: string
): FormikValidationResult {
  if (!validDate || !validTime) return text
  const [hour, minute] = validTime.split(':')
  const now = DateTime.now()
  const date = new Date(validDate)
  const dateInFuture = isAfter(date, now.toJSDate())
  const dateIsToday = isSameDay(date, now.toJSDate())
  const timeInFuture =
    now.hour < Number.parseInt(hour) || (now.hour === Number.parseInt(hour) && now.minute <= Number.parseInt(minute))

  return dateInFuture || (dateIsToday && timeInFuture) ? undefined : text
}
const TIMESTAMP_DATE_FORMAT = 'yyyy-MM-dd'
const TIMESTAMP_TIME_FORMAT = 'HH:mm'

export function formikDateTimeIsNotBeforeSecondDateTime(
  validDate: string,
  validTime: string,
  secondDate: string,
  secondTime: string,
  text: string
): FormikValidationResult {
  const validDateTime = DateTime.fromFormat(
    `${validDate} ${validTime}`,
    `${TIMESTAMP_DATE_FORMAT} ${TIMESTAMP_TIME_FORMAT}`
  ).toISO()

  const secondDateTime = DateTime.fromFormat(
    `${secondDate} ${secondTime}`,
    `${TIMESTAMP_DATE_FORMAT} ${TIMESTAMP_TIME_FORMAT}`
  ).toISO()

  return secondDateTime && validDateTime && secondDateTime > validDateTime ? undefined : text
}

export function formikTimeIsNotBeforeSecondTime(
  validTime: string,
  secondTime: string,
  text: string
): FormikValidationResult {
  return secondTime > validTime ? undefined : text
}

export const formikTimeIsNotInServiceWindow = (
  timeFrom: string,
  timeTo: string,
  validTime: string,
  text: string
): FormikValidationResult => (timeFrom <= validTime && validTime <= timeTo ? undefined : text)

export const formikIsValidNorwegianPostalCode = (postCode: string, text: string): FormikValidationResult =>
  /^\d{4}$/i.test(postCode) ? undefined : text

export const formikValidateSeveral = (validations: FormikValidationResult[]) => {
  return validations.find((validation) => !!validation)
}

export const hexRegex = /^#[A-F0-9]{6}$/i

export const formikValidHEX = (value: string | undefined, errorMessage: string) =>
  value && hexRegex.test(value) ? undefined : errorMessage

export const formikIsGreaterOrEqualThan = (
  value: unknown[],
  amount: number,
  errorMessage: string
): FormikValidationResult => {
  return value.length >= amount ? undefined : errorMessage
}

const getHoursMinutes = (time: string) => {
  const hourMinutes = time.split(':')
  return {
    hours: Number(hourMinutes[0]),
    minutes: Number(hourMinutes[1])
  }
}

export const isStartTimeBeforeEndTimeValidation = (
  startTime: string | undefined,
  endTime: string | undefined,
  errorMessage: string,
  startDate?: string | DateTime,
  endDate?: string | DateTime
) => {
  if (!startTime || !endTime) return undefined
  const start = getHoursMinutes(startTime)
  const end = getHoursMinutes(endTime)

  if (
    Number.isNaN(start.hours) ||
    Number.isNaN(start.minutes) ||
    Number.isNaN(end.hours) ||
    Number.isNaN(end.minutes)
  ) {
    console.error('Got NaN when parsing start and end time', start, startTime, end, endTime)
    return undefined
  }

  const startDay = startOfDay(startDate)
  const endDay = endDate ? startOfDay(endDate) : startDay
  const timeFrom = startDay.set({ hour: start.hours, minute: start.minutes })
  const timeTo = endDay.set({ hour: end.hours, minute: end.minutes })
  return timeFrom < timeTo ? undefined : errorMessage
}
